// The buy box.
//
// Shopify owns the data and the transaction; this owns the interface. The
// variants, their prices and their selling plan allocations are rendered into
// the page as JSON by the section — already formatted as money, because a
// storefront selling in eleven currencies should not carry a JS money
// formatter. Adding to cart goes through /cart/add.js and then hands off to
// Horizon's own cart drawer through the standard event it already listens for.
//
// That event class is imported at the point of use rather than at the top of
// the file. A top-level import of a CDN module makes the whole module throw
// before anything runs if the network hiccups — which would leave the buy box
// standing there with no prices, no swatch colours and an empty stage, looking
// for all the world like it had disappeared. Nothing about choosing a flavour
// should depend on a module only the add-to-cart path needs.

class PruvitBuy {
  constructor(root) {
    this.root = root;
    this.variants = JSON.parse(root.querySelector('[data-pruvit-buy-variants]').textContent);
    this.flavors = JSON.parse(root.querySelector('[data-pruvit-buy-flavors]').textContent);

    // Liquid option positions are 1-based; array indices are not.
    this.idx = {
      flavor: parseInt(root.dataset.optionIndexFlavor, 10) - 1,
      formula: parseInt(root.dataset.optionIndexFormula, 10) - 1,
      size: parseInt(root.dataset.optionIndexSize, 10) - 1,
    };

    // Hidden option values are dropped in Liquid, so "the first one rendered"
    // is the only reliable default — forloop.first would have marked a pill
    // that is not on the page.
    // Frequency is Liquid's call, not this file's — a VIP starts on one-time
    // because they already hold a subscription. Read rather than assumed:
    // hardcoding 'sub' here put the state back the moment the script ran, so
    // the card looked selected and the add-to-cart disagreed with it.
    // The value is 'once' or 'plan:<id>' — one radio per plan the product
    // offers, since a product can offer several (Pulse Pro is monthly and
    // annual). The fallback is the first radio rendered rather than a
    // hardcoded name, because which options exist is Liquid's call: a product
    // that requires a selling plan has no one-time radio at all.
    const freqSet = root.querySelector('[data-pruvit-opts="freq"]');
    const freqOn = freqSet?.querySelector('[aria-checked="true"]') || freqSet?.querySelector('.pruvit-freqo');
    this.state = { flavor: null, formula: null, size: null, freq: freqOn?.dataset.value || 'once' };

    // Product-page syncing. Off everywhere else — see the comment on the
    // attributes in pruvit-buy-core.
    this.syncsPage = root.hasAttribute('data-pruvit-sync-page');
    try {
      this.optionNames = JSON.parse(root.dataset.optionNames || '[]');
    } catch {
      this.optionNames = [];
    }
    this.syncedVariantId = null;
    this.syncAbort = null;

    // The multi-buy pack (pruvit-buy-core), when an offer is running. Liquid
    // only renders it inside the offer's dates, but a page can be served from
    // cache or left open past the end, and quoting a saving the discount no
    // longer gives is the one thing this must not do.
    const multi = root.querySelector('.pruvit-pill-opt--multi');
    if (multi && parseInt(multi.dataset.ends, 10) * 1000 <= Date.now()) multi.remove();
    // The gift with purchase, for the same reason: past its end on a cached
    // page, the line and the chips go rather than promise a gift Easy Gift
    // will no longer add.
    const gift = root.querySelector('[data-pruvit-gift]');
    if (gift && parseInt(gift.dataset.ends, 10) * 1000 <= Date.now()) {
      gift.remove();
      for (const chip of root.querySelectorAll('.pruvit-gift-chip')) chip.remove();
      for (const pill of root.querySelectorAll('[data-gift]')) pill.removeAttribute('data-gift');
    }
    // How many of the variant go in, and the offer quoted on them, if any.
    this.state.qty = 1;
    this.multi = null;

    for (const key of ['flavor', 'formula', 'size']) {
      const group = root.querySelector(`[data-pruvit-opts="${key}"]`);
      if (!group) continue;
      const pressed = group.querySelector('[aria-pressed="true"]') || group.querySelector('button');
      if (!pressed) continue;
      pressed.setAttribute('aria-pressed', 'true');
      this.state[key] = pressed.dataset.value;
    }

    // What comes off a one-time purchase for this viewer: a VIP's recurring
    // rate, or the joining rate where they have a subscription in the cart.
    // Either way it is applied at cart level by an app, so the variant prices
    // above are the undiscounted ones and what they will be charged has to be
    // worked out here. Absent for everyone else, which makes the rate 0 and
    // every sum below a no-op.
    this.orderRate = parseFloat(root.dataset.orderRate || 0) || 0;

    // The add-to-cart label, read before anything has a chance to overwrite
    // it. renderCta reached for `cta.dataset.label` and that attribute has
    // never existed on the button, so the `|| text.textContent` fallback was
    // the only branch that ever ran — and by then the text was whatever the
    // last render left there. Pick a sold-out flavour and then an available
    // one and the button stayed reading "Sold out" while being perfectly
    // clickable, which is the worst of the three states it could be in.
    this.ctaLabel = (this.root.querySelector('[data-pruvit-cta-text]')?.textContent || '').trim();

    this.view = 'pack';
    this.bind();
    this.paintSwatches();
    this.render();
  }

  bind() {
    this.root.addEventListener('click', (event) => {
      const opt = event.target.closest('[data-pruvit-opts] button');
      if (opt) return this.chooseOption(opt);

      const thumb = event.target.closest('[data-pruvit-thumbs] button');
      if (thumb) return this.chooseView(thumb.dataset.view);

      if (event.target.closest('[data-pruvit-add]')) return this.add();
      if (event.target.closest('[data-pruvit-sf]')) return this.openLabel();
    });

    const dialog = this.dialog();
    if (dialog) {
      // Captured before anything rewrites it: the setting's wording is the base
      // every per-variant description is built from.
      const img = dialog.querySelector('[data-pruvit-sf-img]');
      this.labelAlt = (img && img.getAttribute('alt')) || '';

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-pruvit-sf-close]')) return dialog.close();
        const tab = event.target.closest('[data-pruvit-sf-formula] button');
        if (tab) {
          this.sfFormula = tab.dataset.value;
          this.renderLabel();
        }
      });
      dialog.addEventListener('change', (event) => {
        if (event.target.closest('[data-pruvit-sf-flavor]')) this.renderLabel();
      });
    }
  }

  chooseOption(button) {
    const group = button.closest('[data-pruvit-opts]');
    const key = group.dataset.pruvitOpts;
    const radio = group.getAttribute('role') === 'radiogroup';
    for (const sibling of group.querySelectorAll('button')) {
      const on = sibling === button;
      sibling.setAttribute(radio ? 'aria-checked' : 'aria-pressed', String(on));
    }
    this.state[key] = button.dataset.value;
    // The multi-buy pack is a size with the same value as a real one, so the
    // variant does not change -- only how many of it, and the offer on them.
    if (key === 'size') {
      const qty = parseInt(button.dataset.qty, 10);
      this.state.qty = qty > 1 ? qty : 1;
      this.multi = qty > 1 ? { qty, percent: parseInt(button.dataset.percent, 10) || 0 } : null;
    }
    // The pack and the label both belong to the variant, so a change of
    // variant invalidates whichever of those is on the stage. The editorial
    // frames do not, so they are left alone.
    if (key !== 'freq' && (this.view === 'pack' || this.view === 'label' || this.view === 'art')) {
      this.view = key === 'flavor' ? 'pack' : this.view;
    }
    this.render();
  }

  chooseView(view) {
    this.view = view;
    this.render();
  }

  /** The variant matching every currently selected option, or null. */
  current() {
    return (
      this.variants.find((v) => {
        if (this.state.flavor && v.options[this.idx.flavor] !== this.state.flavor) return false;
        if (this.state.formula && v.options[this.idx.formula] !== this.state.formula) return false;
        if (this.state.size && v.options[this.idx.size] !== this.state.size) return false;
        return true;
      }) || null
    );
  }

  /**
   * The plan to quote. The storefront exposes every plan in the group,
   * including rates that belong to existing and legacy SmartShip customers —
   * so picking the cheapest would advertise a legacy 30% rate to a first-time
   * visitor, which is both wrong and a price claim we cannot stand behind.
   * The DEFAULT segment is what someone joining today actually gets, and it is
   * the rate the copy beside it quotes. The app settles the real rate at
   * checkout from who the customer is.
   */
  plan(variant) {
    if (!variant || !variant.plans.length) return null;
    return variant.plans.find((p) => p.segment === 'DEFAULT') || variant.plans[0];
  }

  /**
   * Every plan a first-time visitor is offered — the DEFAULT segment, or all
   * of them when the product's plans carry no segment at all. This is the list
   * the Frequency step renders; plan() above is only which of them is quoted
   * by default.
   */
  offeredPlans(variant) {
    if (!variant || !variant.plans.length) return [];
    const preferred = variant.plans.filter((p) => p.segment === 'DEFAULT');
    return preferred.length ? preferred : variant.plans;
  }

  /**
   * The plan the shopper has actually chosen, or null for a one-time purchase.
   *
   * Falls back to the default plan when the selected id is not on this
   * variant. Selling plan ids are stable across a product's variants today, so
   * that is a guard rather than a path — but adding a line with a plan id the
   * variant does not carry is a 422, and silently reverting to the plan the
   * box is quoting is better than that.
   */
  chosenPlan(variant) {
    if (this.state.freq === 'once') return null;
    const id = String(this.state.freq).slice(5);
    const plans = this.offeredPlans(variant);
    return plans.find((p) => String(p.id) === id) || this.plan(variant);
  }

  paintSwatches() {
    for (const button of this.root.querySelectorAll('[data-pruvit-opts="flavor"] button')) {
      const data = this.flavors[button.dataset.value];
      if (!data) continue;
      const disc = button.querySelector('[data-pruvit-sw-disc]');
      disc.style.setProperty('--fc', data.swatch);
      if (data.art) {
        const img = disc.querySelector('img');
        img.src = data.art;
        img.hidden = false;
      }
      if (data.badge) {
        const badge = button.querySelector('[data-pruvit-sw-badge]');
        badge.textContent = data.badge;
        badge.hidden = false;
      }
    }
  }

  render() {
    const variant = this.current();
    const flavor = this.flavors[this.state.flavor] || {};

    // The stage takes the flavour's ground so a transparent pack PNG sits on
    // colour rather than on the section, and clicking from the pack to the
    // flavour frame does not change what is underneath it.
    const ground = flavor.ground || flavor.swatch;
    if (ground) this.root.style.setProperty('--pruvit-flavor-ground', ground);

    this.renderGallery(variant, flavor);
    this.renderSizes();
    this.renderGift();
    this.renderAvailability();
    this.renderFrequency(variant);
    this.renderCta(variant);
    this.syncPage(variant);
  }

  /**
   * Tell the rest of the product page which variant is selected.
   *
   * Horizon's own variant-picker is the thing normally doing this, and every
   * other block on a product page is built around it: media-gallery, price,
   * sku and inventory each listen for shopify:product:select and then await
   * `event.promise` for the re-rendered section's HTML to morph themselves
   * from. So replacing the picker means taking on its side of that contract,
   * not just its markup — otherwise the gallery keeps showing the first
   * flavour's pack while the buy box sells a different one.
   *
   * The shape here is variant-picker.js's: dispatch immediately with a
   * deferred promise, fetch, then resolve with { variant, detail: { html, … } }.
   * `resource` is null because that JSON comes from the variant-picker markup
   * this box replaces — Horizon already has a branch for that case and the
   * listeners that matter only read `html`.
   */
  syncPage(variant) {
    if (!this.syncsPage) return;
    const id = variant && variant.id;
    if (!id) return;

    // First render is the state the page was already server-rendered in, and
    // re-fetching it would be a round trip to learn nothing.
    if (this.syncedVariantId === null) {
      this.syncedVariantId = id;
      return;
    }
    if (id === this.syncedVariantId) return;
    this.syncedVariantId = id;

    // Clicking through flavours faster than the network answers should leave
    // the page on the last one clicked, not on whichever reply lands last.
    this.syncAbort?.abort();
    this.syncAbort = new AbortController();
    const signal = this.syncAbort.signal;

    const root = this.root;
    const url = `${root.dataset.productUrl}?variant=${id}`;
    const selectedOptions = (variant.options || []).map((value, i) => ({
      name: this.optionNames[i] || `Option ${i + 1}`,
      value: String(value ?? ''),
    }));

    // ---- the sheen -------------------------------------------------------
    // Measured 2026-09-28: 423ms from clicking a flavour to the image
    // changing, 313ms of it this fetch. That is long enough to read as a dead
    // click, so the stage says it is working.
    //
    // The flag goes on product-component, not on the buy box: the gallery is
    // the box's SIBLING in the product grid, so the box cannot reach it. A
    // compact box -- the options drawer, a card -- has no gallery above it and
    // no product-component either, so scope is null there and nothing happens.
    const scope = root.closest('product-component');
    let sheenOff = () => {};
    if (scope) {
      const stop = () => {
        // Only if this sync is still the current one. Clicking through
        // flavours aborts the older fetches, and an aborted one must not
        // switch off an indicator the newest one turned on.
        if (this.syncedVariantId === id) scope.removeAttribute('data-pruvit-syncing');
      };
      scope.setAttribute('data-pruvit-syncing', '');
      // Lifted when the gallery actually changes rather than when the fetch
      // returns -- there is still parsing, a DOM swap and an image load after
      // that, and a sheen that stops before the picture moves is worse than
      // none. The timeout is the stuck-guard: whatever happens, this comes off.
      const watch = scope.querySelector('.product-information__media');
      const timer = setTimeout(stop, 4000);
      let observer = null;
      sheenOff = () => {
        clearTimeout(timer);
        observer?.disconnect();
        stop();
      };
      if (watch) {
        observer = new MutationObserver(() => sheenOff());
        observer.observe(watch, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'srcset'],
        });
      }
    }

    const run = async () => {
      // Imported here, not at the top: a CDN module that fails to load must
      // not take the whole box down with it. Same reason as the cart event.
      const { ProductSelectEvent } = await import('@shopify/events');
      const deferred = ProductSelectEvent.createPromise();

      root.dispatchEvent(
        new ProductSelectEvent({
          product: {
            id: root.dataset.productId ?? '',
            title: root.dataset.productTitle ?? '',
            handle: root.dataset.productHandle ?? '',
          },
          selectedOptions,
          detail: { variantId: String(id) },
          promise: deferred.promise,
        })
      );

      try {
        const response = await fetch(url, { signal });
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
        deferred.resolve({
          variant: null,
          detail: {
            html,
            productId: root.dataset.productId ?? '',
            sourceId: String(id),
            resource: null,
          },
        });
      } catch (error) {
        deferred.reject(error);
        throw error;
      }
    };

    run().catch((error) => {
      // A failed or aborted sync must take the sheen with it. On the happy
      // path the observer above has usually lifted it already.
      sheenOff();
      if (error?.name !== 'AbortError') console.warn('[pruvit-buy] could not sync the page:', error);
    });
  }

  renderGallery(variant, flavor) {
    // Compact mode has no gallery — the options drawer wants the form and the
    // stage would be a second picture of what the card already showed. Without
    // this the first render threw on a null stage and took the whole box with
    // it, controls and all.
    if (!this.root.querySelector('[data-pruvit-stage]')) {
      const sf = this.root.querySelector('[data-pruvit-sf]');
      if (sf) sf.hidden = true;
      this.labelSrc = null;
      return;
    }

    const sources = {
      pack: variant && variant.pack,
      label: variant && variant.label,
      art: flavor.art || null,
    };

    for (const thumb of this.root.querySelectorAll('[data-pruvit-thumbs] button')) {
      const view = thumb.dataset.view;
      if (view in sources) {
        const src = sources[view];
        // Hidden with its view rather than left holding the previous
        // flavour's picture — a flavour with no back label must not show
        // another flavour's.
        thumb.hidden = !src;
        if (src) thumb.querySelector('img').src = src;
      }
      thumb.setAttribute('aria-selected', String(view === this.view));
    }

    // If the chosen view just disappeared, fall back to the pack.
    const active = this.root.querySelector(`[data-pruvit-thumbs] [data-view="${this.view}"]`);
    if (!active || active.hidden) this.view = 'pack';

    const stage = this.root.querySelector('[data-pruvit-stage]');
    const stageImg = this.root.querySelector('[data-pruvit-stage-img]');
    const chosen = this.root.querySelector(`[data-pruvit-thumbs] [data-view="${this.view}"]`);
    const chosenImg = chosen && chosen.querySelector('img');
    if (chosenImg && chosenImg.src) {
      stageImg.src = chosenImg.src;
      stageImg.alt = chosenImg.alt || '';
    }
    // Editorial frames fill the stage and carry their own ground; pack shots
    // and labels float on the flavour ground with room around them.
    stage.classList.toggle('pruvit-buy__stage--photo', this.view === 'mix' || this.view === 'ritual');

    // The modal can reach every flavour's panel now, so the way in is offered
    // whenever the product has one at all. The gallery thumbnail still hides
    // with the variant — that stage must never show another flavour's label.
    const sf = this.root.querySelector('[data-pruvit-sf]');
    if (sf) sf.hidden = !this.variants.some((v) => v.label);
    this.labelSrc = sources.label;
  }

  renderSizes() {
    for (const button of this.root.querySelectorAll('[data-pruvit-opts="size"] button')) {
      // Each option is only a constraint if the product has it. A product with
      // no Flavor group has no state.flavor, and comparing against it matched
      // nothing — so the savings came out blank on every product but K_1.
      const probe = this.variants.find(
        (v) =>
          (!this.state.flavor || v.options[this.idx.flavor] === this.state.flavor) &&
          (!this.state.formula || v.options[this.idx.formula] === this.state.formula) &&
          v.options[this.idx.size] === button.dataset.value
      );
      const save = button.querySelector('[data-pruvit-size-save]');
      if (!save) continue;
      // The saving is the real compare-at gap, not a number typed into a
      // setting — so it cannot drift from what the customer is charged.
      if (probe && probe.compare > probe.price) {
        save.textContent = pruvitT('savePercent', 'Save %PERCENT%%', {
          PERCENT: Math.round((1 - probe.price / probe.compare) * 100)
        });
      } else {
        save.textContent = '';
      }
    }
  }

  /**
   * The gift line follows the pressed size: how to qualify until a size that
   * earns the gift is chosen, then that it is included. The pill carries
   * data-gift when its servings (times boxes, for the multi-buy pack) reach
   * the offer's minimum -- worked out in Liquid, so there is no rule here.
   */
  renderGift() {
    const line = this.root.querySelector('[data-pruvit-gift]');
    if (!line) return;
    const on = !!this.root.querySelector('[data-pruvit-opts="size"] [aria-pressed="true"][data-gift]');
    line.classList.toggle('is-on', on);
    line.querySelector('[data-pruvit-gift-on]').hidden = !on;
    line.querySelector('[data-pruvit-gift-off]').hidden = on;
  }

  /**
   * Mark the option values you cannot buy.
   *
   * The CTA already said "Sold out", which told you the answer only after
   * you had picked the flavour — so the one control that knew was the last
   * one to say so, and the discs and pills all looked equally buyable.
   *
   * Availability is per combination, not per value: White Peach is in stock
   * Caffeine Free and gone Charged, so the marks move as the other groups
   * change. That is the same rule Shopify's own pickers use, and the only
   * one that can be right — a flavour is not sold out, a variant is.
   *
   * Two states, because they are two different facts. `sold` is a variant
   * that exists and has none left; `gone` is a combination this product
   * never had. They look the same and read differently, which is the right
   * way round: a shopper wants one glance, a screen reader wants the word.
   *
   * Nothing is disabled. You can still select a sold-out flavour and see it
   * on the stage with the label and the facts panel, which is what a shopper
   * deciding whether to wait for it actually wants — and it is the state
   * Craig was looking at when he asked for this.
   */
  renderAvailability() {
    for (const key of ['flavor', 'formula', 'size']) {
      const group = this.root.querySelector(`[data-pruvit-opts="${key}"]`);
      if (!group) continue;

      for (const button of group.querySelectorAll('button')) {
        const want = { ...this.state, [key]: button.dataset.value };
        // Each option only constrains if the product has it — the same guard
        // renderSizes carries, for the same reason.
        const matching = this.variants.filter(
          (v) =>
            (!want.flavor || v.options[this.idx.flavor] === want.flavor) &&
            (!want.formula || v.options[this.idx.formula] === want.formula) &&
            (!want.size || v.options[this.idx.size] === want.size)
        );
        const state = matching.some((v) => v.available) ? null : matching.length ? 'sold' : 'gone';

        if (state) button.dataset.oos = state;
        else delete button.dataset.oos;

        let note = button.querySelector('[data-pruvit-oos-note]');
        if (state) {
          if (!note) {
            note = document.createElement('span');
            note.className = 'pruvit-sr-only';
            note.dataset.pruvitOosNote = '';
            button.append(note);
          }
          note.textContent = ` (${state === 'sold' ? this.root.dataset.soldOut : this.root.dataset.unavailable})`;
        } else if (note) {
          note.remove();
        }
      }
    }
  }

  /**
   * What a one-time order of this variant actually costs the viewer: the
   * variant price, less whatever rate they have earned. Held in integer
   * cents the way Shopify holds it, so 15080 at 25% off is exactly 11310 and
   * not a cent either side of it.
   */
  oncePrice(variant) {
    if (!variant) return 0;
    if (!this.orderRate) return variant.price;
    // Liquid's figure where it sent one: the rate taken off the pre-VAT price
    // and VAT added after, as Shopify prices a plan. The same sum on the
    // VAT-inclusive price below rounds differently and sat a cent off the
    // plan's own price (Italy, 2026-10-20).
    if (typeof variant.once === 'number') return variant.once;
    return Math.round((variant.price * (100 - this.orderRate)) / 100);
  }

  /**
   * The same amount as text. Liquid's own string is returned untouched
   * wherever nothing has come off the price, because money() rebuilds the
   * number from a sample and cannot reconstruct a thousands separator or a
   * comma decimal mark — passing an unchanged EUR price through it turned
   * "1.234,56 €" into "1234.56 €".
   */
  onceText(variant) {
    if (!variant) return '';
    const now = this.oncePrice(variant);
    return now === variant.price ? variant.priceText : this.money(now, variant.priceText);
  }

  renderFrequency(variant) {
    const set = this.root.querySelector('[data-pruvit-freqset]');
    const plans = this.offeredPlans(variant);
    // No selling plans on this variant means there is no decision to offer.
    // The choice the shopper had is kept, not dropped: a flavour outside
    // SmartShip (Sour Candy at launch) forced 'once', and on switching to a
    // flavour with plans the SmartShip card still LOOKED chosen while the
    // state said one-time -- so Add to cart added it one-time (Craig,
    // 2026-10-27). Restore it when plans come back, and repaint from state
    // every time, so what is shown is what is added.
    set.hidden = !plans.length;
    if (!plans.length) {
      if (this.state.freq !== 'once') this.freqHeld = this.state.freq;
      this.state.freq = 'once';
      return;
    }
    if (this.freqHeld) {
      const held = this.freqHeld;
      this.freqHeld = null;
      this.state.freq = plans.some((p) => `plan:${p.id}` === held)
        ? held
        : `plan:${(this.plan(variant) || plans[0]).id}`;
    }
    this.paintFrequency();

    // The one-time option is rendered by Liquid from the FIRST variant, and a
    // product can mix: one variant requiring a plan and another not. Hide it
    // per variant rather than trusting the first, and move off it if it was
    // the selection.
    const onceBtn = this.root.querySelector('[data-pruvit-opts="freq"] [data-value="once"]');
    const onceWrap = onceBtn?.closest('.pruvit-freqo-wrap');
    if (onceWrap) onceWrap.hidden = !!variant.requiresPlan;
    if (variant.requiresPlan && this.state.freq === 'once') {
      this.state.freq = `plan:${(this.plan(variant) || plans[0]).id}`;
      this.paintFrequency();
    }

    const now = this.oncePrice(variant);
    const oncePrice = this.root.querySelector('[data-pruvit-once-price]');
    if (oncePrice) oncePrice.textContent = this.onceText(variant);

    // The reduced price needs its own struck original and its own saving:
    // nothing else on the page would account for the smaller number.
    const wasOnce = this.root.querySelector('[data-pruvit-once-was]');
    const saveOnce = this.root.querySelector('[data-pruvit-once-save]');
    const orderOff = variant ? variant.price - now : 0;
    if (wasOnce) {
      wasOnce.textContent = orderOff > 0 ? variant.priceText : '';
      wasOnce.hidden = orderOff <= 0;
    }
    if (saveOnce) {
      saveOnce.textContent =
        orderOff > 0 ? pruvitT('saveAmount', 'Save %AMOUNT%', { AMOUNT: this.money(orderOff, variant.priceText) }) : '';
    }

    // Each plan quotes the price Shopify states for it, VIP or not. Whether a
    // VIP rate beats or stacks with a plan's own joining rate has not been
    // observed on a real account, and the box does not invent a number for a
    // line it cannot verify.
    for (const plan of plans) {
      const price = this.root.querySelector(`[data-pruvit-plan-price="${plan.id}"]`);
      const save = this.root.querySelector(`[data-pruvit-plan-save="${plan.id}"]`);
      const was = this.root.querySelector(`[data-pruvit-plan-was="${plan.id}"]`);
      const off = variant ? variant.price - plan.price : 0;
      if (price) price.textContent = plan.priceText;
      if (save) {
        save.textContent = off > 0 ? pruvitT('saveAmount', 'Save %AMOUNT%', { AMOUNT: this.money(off, variant.priceText) }) : '';
      }
      // The one-time price it is off, struck. Shown even where the saving is
      // hidden (someone who already subscribes), as the Liquid renders it.
      if (was) {
        was.textContent = off > 0 ? variant.priceText : '';
        was.hidden = off <= 0;
      }
    }
  }

  /** Repaint the radios from state, for the times this file changes it. */
  paintFrequency() {
    for (const button of this.root.querySelectorAll('[data-pruvit-opts="freq"] .pruvit-freqo')) {
      button.setAttribute('aria-checked', String(button.dataset.value === this.state.freq));
    }
  }

  /** Format a minor-unit amount using the shape Liquid already gave us. */
  money(amount, sample) {
    // Liquid's string is the shape, separators and all. Learn them from it
    // rather than assuming a point and no grouping: Germany renders this shop's
    // prices as €74,94, and rebuilding the number with toFixed put
    // "Save €11.24" beside it — a decimal point in a market that uses a
    // comma, in the one place a shopper is comparing two amounts.
    const digits = (sample.match(/\d[\d.,\u00a0\u202f\u2009 ]*\d|\d/) || ['0'])[0];
    const cents = /[.,]\d{2}$/.test(digits);
    const decimal = cents ? digits.slice(-3, -2) : '';
    // Whatever separators the sample used, minus the decimal one: what is left
    // is the thousands separator, and nothing left means it does not group.
    // Spaces count. French renders EUR as "1 234,56 €" with a
    // no-break space doing the grouping, and dropping it gave "1234,56 €".
    const seps = digits.replace(/\d/g, '');
    const group = seps.length > (cents ? 1 : 0) ? seps[0] : '';

    const parts = (amount / 100).toFixed(cents ? 2 : 0).split('.');
    let whole = parts[0];
    if (group) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
    return sample.replace(digits, whole + (parts[1] ? decimal + parts[1] : ''));
  }

  renderCta(variant) {
    const cta = this.root.querySelector('[data-pruvit-add]');
    const text = this.root.querySelector('[data-pruvit-cta-text]');
    const total = this.root.querySelector('[data-pruvit-total]');
    const was = this.root.querySelector('[data-pruvit-was]');
    const plan = this.chosenPlan(variant);
    const subscribing = this.state.freq !== 'once' && plan;

    if (!variant) {
      cta.setAttribute('aria-disabled', 'true');
      text.textContent = this.root.dataset.unavailable;
      total.textContent = '';
      was.hidden = true;
      return;
    }
    if (!variant.available) {
      cta.setAttribute('aria-disabled', 'true');
      text.textContent = this.root.dataset.soldOut;
      total.textContent = '';
      was.hidden = true;
      return;
    }

    cta.removeAttribute('aria-disabled');
    text.textContent = cta.dataset.label || this.ctaLabel || text.textContent;

    // The multi-buy: the total for all the boxes with the offer off, against
    // the same boxes at full price. Only for a one-time purchase by someone
    // with no rate of their own. The offer is an app discount the theme cannot
    // read, and whether it stacks with a VIP's rate or a plan's has not been
    // observed -- so for them the pack still says what it saves and the button
    // names no number it cannot stand behind (Craig agreed, 2026-10-15).
    if (this.multi) {
      text.textContent = pruvitT('multibuyAdd', `Add ${this.multi.qty} to cart`, { COUNT: this.multi.qty });
      if (subscribing || this.orderRate) {
        total.textContent = '';
        was.hidden = true;
        return;
      }
      const full = variant.price * this.multi.qty;
      const offer = Math.round((full * (100 - this.multi.percent)) / 100);
      total.textContent = this.money(offer, variant.priceText);
      was.textContent = this.money(full, variant.priceText);
      was.hidden = false;
      return;
    }

    const now = this.oncePrice(variant);
    total.textContent = subscribing ? plan.priceText : this.onceText(variant);
    let struck = null;
    if (subscribing) {
      struck = variant.priceText;
    } else if (now < variant.price) {
      struck = variant.priceText;
    } else if (variant.compare > variant.price) {
      struck = variant.compareText;
    }
    was.textContent = struck || '';
    was.hidden = !struck;
  }

  async add() {
    const cta = this.root.querySelector('[data-pruvit-add]');
    if (cta.getAttribute('aria-disabled') === 'true' || cta.hasAttribute('loading')) return;
    const variant = this.current();
    if (!variant) return;

    const plan = this.chosenPlan(variant);
    const body = { items: [{ id: variant.id, quantity: this.state.qty || 1 }] };
    // A variant that requires a selling plan is refused without one — 422
    // "Variant can only be purchased with a selling plan" — so the plan goes
    // on whenever one is chosen, and Liquid does not offer one-time for those.
    if (plan && (this.state.freq !== 'once' || variant.requiresPlan)) {
      body.items[0].selling_plan = plan.id;
    }

    cta.setAttribute('loading', '');
    const error = this.root.querySelector('[data-pruvit-error]');
    error.hidden = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || pruvitT('addFailed', 'Could not add that.'));

      // Horizon's cart icon and drawer both listen for this. Dispatching it is
      // what keeps the count and the drawer contents honest without this file
      // knowing anything about either. The plain CustomEvent is the fallback:
      // same type name, so the listeners still fire, just without the class's
      // extra payload.
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', source: 'pruvit-buy' }));
      } catch {
        document.dispatchEvent(
          new CustomEvent('shopify:cart:lines-update', { detail: { action: 'add', source: 'pruvit-buy' } })
        );
      }

      // Hand the drawer the line Shopify just confirmed, so it can draw the row
      // on this answer instead of opening empty and fetching the cart to find
      // out what it already knows. Deliberately a separate, theme-owned event:
      // the standard one above is what apps listen to and it keeps its shape.
      document.dispatchEvent(
        new CustomEvent('pruvit:cart:added', { detail: { items: payload.items || [], source: 'pruvit-buy' } })
      );

      // Our own drawer. This used to look for Horizon's [aria-controls=cart-drawer]
      // trigger, which stopped existing the moment the header stopped using
      // Horizon's drawer — so the item went in and nothing appeared to happen.
      const trigger = document.querySelector('[data-pruvit-cart-open]');
      if (trigger) trigger.click();
    } catch (e) {
      error.textContent = e.message;
      error.hidden = false;
    } finally {
      cta.removeAttribute('loading');
    }
  }

  dialog() {
    return this.root.parentElement.querySelector('[data-pruvit-sf-dialog]');
  }

  /**
   * Opens on whatever the buy box has selected, and is free to move from there.
   * Nothing it does touches this.state: comparing two panels is not choosing
   * between them, and a reader who closes the modal should find the buy box
   * exactly as they left it.
   */
  openLabel() {
    const dialog = this.dialog();
    if (!dialog) return;
    const flavors = dialog.querySelector('[data-pruvit-sf-flavor]');
    if (flavors && this.state.flavor) flavors.value = this.state.flavor;
    this.sfFormula = this.state.formula;
    this.renderLabel();
    dialog.showModal();
  }

  renderLabel() {
    const dialog = this.dialog();
    if (!dialog) return;
    const stage = dialog.querySelector('[data-pruvit-sf-wrap]');
    const img = dialog.querySelector('[data-pruvit-sf-img]');
    const missing = dialog.querySelector('[data-pruvit-sf-missing]');
    const status = dialog.querySelector('[data-pruvit-sf-status]');
    const select = dialog.querySelector('[data-pruvit-sf-flavor]');
    const flavor = select ? select.value : this.state.flavor;
    const formula = this.sfFormula;

    for (const tab of dialog.querySelectorAll('[data-pruvit-sf-formula] button')) {
      tab.setAttribute('aria-pressed', String(tab.dataset.value === formula));
    }

    // The panel belongs to the flavour and the formula. Size is not part of the
    // lookup — the same sachet is in every count — so the first variant that
    // carries a panel for this pair answers for all of them.
    const match = this.variants.find(
      (v) =>
        v.label &&
        (!flavor || v.options[this.idx.flavor] === flavor) &&
        (!formula || v.options[this.idx.formula] === formula)
    );

    const describe = [flavor, formula].filter(Boolean).join(' ');
    img.alt = describe ? `${this.labelAlt} — ${describe}` : this.labelAlt;

    if (!match) {
      // Say so plainly rather than leaving a shimmer up or asking the browser
      // for a src that is not there.
      img.removeAttribute('src');
      stage.classList.add('is-ready', 'is-missing');
      if (missing) missing.hidden = false;
      if (status) status.textContent = pruvitT('sfNone', 'No supplement facts panel is published for %FLAVOR%.', { FLAVOR: describe });
      return;
    }

    if (missing) missing.hidden = true;
    stage.classList.remove('is-missing');

    // A cached panel is already complete by the time src is assigned and fires
    // no load event at all, so the complete check is what stops the shimmer
    // sitting over a picture that has already arrived.
    if (img.getAttribute('src') === match.label && img.complete && img.naturalWidth) {
      stage.classList.add('is-ready');
      return;
    }
    stage.classList.remove('is-ready');
    if (status) status.textContent = pruvitT('sfLoading', 'Loading the supplement facts panel.');
    const settled = (message) => {
      stage.classList.add('is-ready');
      if (status) status.textContent = message;
    };
    img.onload = () => settled('');
    img.onerror = () => settled(pruvitT('sfFailed', 'The supplement facts panel could not be loaded.'));
    img.src = match.label;
    if (img.complete && img.naturalWidth) img.onload();
  }
}

function init() {
  for (const root of document.querySelectorAll('[data-pruvit-buy]')) {
    if (root.dataset.pruvitBuyReady) continue;
    root.dataset.pruvitBuyReady = 'true';
    new PruvitBuy(root);
  }
}

init();
document.addEventListener('shopify:section:load', init);
